import React, { useState, useEffect } from 'react';
import { Eyebrow, Btn, Reveal } from './UI';
import SplitText from './SplitText';
import { GridScan } from './GridScan';

/* ─── Single-word focus bracket component ─── */
function FocusWord({
  word,
  isActive,
  blurAmount = 3.5,
  borderColor = '#C94B2A',
  glowColor = 'rgba(201,75,42,0.5)',
  duration = 0.55,
}) {
  const cornerBase = {
    position: 'absolute',
    width: '0.55rem',
    height: '0.55rem',
    border: `2.5px solid ${borderColor}`,
    filter: `drop-shadow(0 0 5px ${glowColor})`,
    borderRadius: 2,
    opacity: isActive ? 1 : 0,
    transition: `opacity ${duration}s ease`,
  };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        color: borderColor,
        filter: isActive ? 'blur(0px)' : `blur(${blurAmount}px)`,
        transition: `filter ${duration}s ease`,
      }}
    >
      {word}
      <span style={{ ...cornerBase, top: -8, left: -8, borderRight: 'none', borderBottom: 'none' }} />
      <span style={{ ...cornerBase, top: -8, right: -8, borderLeft: 'none', borderBottom: 'none' }} />
      <span style={{ ...cornerBase, bottom: -8, left: -8, borderRight: 'none', borderTop: 'none' }} />
      <span style={{ ...cornerBase, bottom: -8, right: -8, borderLeft: 'none', borderTop: 'none' }} />
    </span>
  );
}

/* ─── Hero ─── */
export default function Hero() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % 2), (0.6 + 1.8) * 1000);
    return () => clearInterval(id);
  }, []);

  const H1 = {
    fontFamily: 'Cardo, Georgia, serif',
    fontSize: 'clamp(38px, 6.2vw, 72px)',
    fontWeight: 400,
    lineHeight: 1.08,
    letterSpacing: '-0.02em',
    color: '#FFFFFF',
    margin: 0,
    textAlign: 'center',
  };

  return (
    <section id="top" style={{ background: '#0C1522' }}>

      {/* ── Hero block with GridScan background ── */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          minHeight: 600,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* GridScan background */}
        <div className="hero-gridscan-bg" style={{ position: 'absolute', inset: 0, zIndex: 0, width: '100%', height: '100%' }}>
          <GridScan
            sensitivity={0.55}
            lineThickness={1}
            linesColor="#2F293A"
            gridScale={0.1}
            scanColor="#000000ff"
            scanOpacity={0.20}
            enablePost
            bloomIntensity={0.6}
            chromaticAberration={0.002}
            noiseIntensity={0.01}
            scanDirection="pingpong"
            scanDuration={2.0}
            scanDelay={2.0}
            scanGlow={0.5}
            scanSoftness={2}
            scanPhaseTaper={0.9}
          />
        </div>
        <style>{`
          @media (min-width: 769px) {
            .hero-gridscan-bg { opacity: 0.45; }
          }
        `}</style>

        {/* Foreground content */}
        <div
          className="container"
          style={{
            position: 'relative',
            zIndex: 1,
            paddingTop: 92,
            paddingBottom: 56,
            textAlign: 'center',
            width: '100%',
          }}
        >
          <div style={{ maxWidth: 760, margin: '0 auto' }}>


            <div style={{ marginBottom: 28 }}>
              <h1 style={H1}>
                <span style={{ display: 'block' }}>
                  Your{' '}
                  <FocusWord word="cameras" isActive={active === 0} />
                </span>

                <span style={{ display: 'block', marginBottom: '0.04em' }}>
                  already see{' '}
                  <FocusWord word="everything." isActive={active === 1} />
                </span>

                <span style={{ display: 'block' }}>
                  Now they can tell you{' '}
                  <em style={{ color: '#C94B2A', fontStyle: 'italic' }}>what matters.</em>
                </span>
              </h1>
            </div>

            {/* Description */}
            <div style={{ maxWidth: 560, margin: '0 auto 40px' }}>
              <SplitText
                tag="p"
                text="TruVue AI connects to the CCTV you already have and turns it into a real-time AI layer — catching safety violations, compliance gaps and security risks the moment they happen, not weeks later in a review."
                delay={10}
                duration={0.7}
                ease="power2.out"
                splitType="words"
                from={{ opacity: 0, y: 20 }}
                to={{ opacity: 1, y: 0 }}
                threshold={0.05}
                rootMargin="0px"
                textAlign="center"
                className="hero-sub"
                style={{ color: '#ffffff', margin: 0 }}
              />
            </div>

            {/* CTAs */}
            <Reveal variant="up" delay={200}>
              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  marginBottom: 20,
                }}
              >
                {/* Solid primary button — white text forced via inline style wrapper */}
                <span style={{ color: '#ffffff' }}>
                  <Btn href="https://wa.me/919425074535" target="_blank" rel="noopener noreferrer">Book a Live Demo</Btn>
                </span>
                {/* Ghost button — white border + text */}
                <span style={{ color: '#ffffff' }}>
                  <Btn href="#how" variant="ghost" style={{ color: '#ffffff', borderColor: 'rgba(255,255,255,0.5)' }}>
                    See How It Works
                  </Btn>
                </span>
              </div>
            </Reveal>

            <Reveal variant="fade" delay={280}>
              <p
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.35)',
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                }}
              >
                {/* No new cameras · No feeds to babysit */}
              </p>
            </Reveal>

          </div>
        </div>
      </div>

    </section>
  );
}